import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  InputAdornment,
  FormControl,
  Chip,
  Switch,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
} from "@mui/material";
import { Add as AddIcon, Description as DescriptionIcon, List as ListIcon, Delete as DeleteIcon } from "@mui/icons-material";
import ReactSelect from "react-select";
import NavigationHeader from "components/NavigationHeader";
import { DashboardStackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import BackdropLoading from "components/BackdropLoading";
import { useAuth } from "shared/hooks/auth";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  obterApontamentos,
  removerApontamento,
  salvarApontamento,
  ICodigoBase,
  IApontamento,
  listarCodigosBase,
  listarComponentesAplicaveis,
  listarLinhasSistema,
  IComponenteAplicavel,
  ILinhaSistemaPadrao,
  IComponenteAgrupado,
  adicionarComponenteAgrupado,
  removerComponenteAgrupado,
  IComponenteBaseAgrupado,
  adicionarComponenteBaseAgrupado,
  removerComponenteBaseAgrupado,
  obterUltimaImportacao,
} from "services/modules/aplicabilidades";
import { ModalApontamento } from "./components/ModalApontamento";
import { ModalAgrupamentoBases } from "./components/ModalAgrupamentoBases";

const SITUACAO_LABEL: Record<number, string> = {
  0: "Em Edição",
  1: "Em Homologação",
  2: "Homologada",
  3: "Disponível Mercado",
};

const SITUACAO_COR: Record<number, string> = {
  0: "#9e9e9e",
  1: "#f57c00",
  2: "#2e7d32",
  3: "#1976d2",
};

const linhasSistemaSelectStyles = {
  control: (base: any) => ({
    ...base,
    backgroundColor: "#EDEDED",
    borderRadius: "4px",
    minHeight: 40,
  }),
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: "#3C90AD",
    borderRadius: "16px",
    paddingLeft: "4px",
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    color: "#fff",
    fontWeight: 500,
    paddingRight: "6px",
  }),
  multiValueRemove: (base: any) => ({
    ...base,
    color: "#1a5a6e",
    borderRadius: "0 16px 16px 0",
    ":hover": {
      backgroundColor: "#337991",
      color: "#1a5a6e",
    },
  }),
  menu: (base: any) => ({ ...base, zIndex: 9999 }),
};

// Plano 14/10/2026 (Frente 6): exclusão mútua "planilha × edição manual" (decisão P2 do PO). Com uma
// planilha de aplicabilidades em andamento na empresa (última importação fora de Disponível Mercado
// e de Removida), o cadastro fica somente leitura. O backend recusa com a mesma mensagem.
const MENSAGEM_PLANILHA_PENDENTE =
  "Há uma planilha de aplicabilidades em andamento. Conclua o processo da planilha ou exclua-a para editar manualmente.";
const STATUS_IMPORTACAO_DISPONIVEL_MERCADO = 3;
const STATUS_IMPORTACAO_REMOVIDA = 4;

const extractApiError = (error: any, fallback: string): string =>
  error?.response?.data?.errors?.Mensagens?.[0] ||
  error?.data?.errors?.Mensagens?.[0] ||
  error?.response?.data?.errors?.[0] ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

// FASE 03 (itens 4/5/7 — 22-06): operações de AGRUPAMENTO pendentes. No modelo de "Salvar
// explícito", agrupar não persiste na hora: a inclusão é refletida na tela (otimista) e a
// operação fica enfileirada para ser aplicada no backend somente ao acionar SALVAR.
type AgrupOpPendente =
  | { tipo: "addComponente"; idAplicabilidade: number; idComponente: number }
  | { tipo: "addBase"; idAplicabilidade: number; idComponenteBase: number };

export const DetalheComponente = () => {
  const { user } = useAuth();
  const { openNotification } = useNavigation();
  const navigate = useNavigate();
  const { empresaId, componenteBaseId } = useParams<{ empresaId: string; componenteBaseId: string }>();
  const location = useLocation();

  const [codigoBaseSelecionado, setCodigoBaseSelecionado] = useState<ICodigoBase | null>(null);
  const [apontamentos, setApontamentos] = useState<IApontamento[]>([]);
  const [itensEmEdicao, setItensEmEdicao] = useState<Array<{
    id: string;
    componenteAplicavel?: IComponenteAplicavel;
    isPadrao: boolean;
    linhasSistema: ILinhaSistemaPadrao[];
    // TAREFA 8.1 (FASE 03): marca itens em staging idênticos a outro (salvo ou pendente).
    isDuplicado?: boolean;
  }>>([]);
  // TAREFA 8.5 (FASE 03): controla salvamento em lote (staging) e o diálogo de confirmação.
  const [isSalvandoLote, setIsSalvandoLote] = useState(false);
  const [confirmarSalvarOpen, setConfirmarSalvarOpen] = useState(false);
  // Frente 9 (02/10/2026, decisão P2): padrão opcional — confirmação quando nenhum apontamento
  // (salvo ativo ou cartão em edição) está marcado como padrão.
  const [confirmarSemPadraoOpen, setConfirmarSemPadraoOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Plano 14/10/2026 (Frente 6): planilha em andamento na empresa → tela somente leitura.
  const [planilhaPendente, setPlanilhaPendente] = useState(false);
  const [modalApontamento, setModalApontamento] = useState<{
    isOpen: boolean;
    apontamento?: IApontamento;
    itemEmEdicaoId?: string;
    isAgrupamento?: boolean;
    isAgrupamentoBases?: boolean; // true = N Bases → 1 Aplicável, false = 1 Base → N Aplicáveis
    idAplicabilidadeParaAgrupar?: number;
  }>({ isOpen: false });
  const [modalAgrupamentoBases, setModalAgrupamentoBases] = useState<{
    isOpen: boolean;
    idAplicabilidadeParaAgrupar?: number;
  }>({ isOpen: false });
  const [linhasSistemaOptions, setLinhasSistemaOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [codigoAplicavelInput, setCodigoAplicavelInput] = useState<{[key: string]: string}>({});

  // FASE 03 (itens 4/5/7 — 22-06): modelo de "Salvar explícito" para edições em apontamentos JÁ
  // salvos. As alterações (padrão, linhas, agrupar) deixam de auto-salvar: ficam refletidas na tela
  // como pendências e só são persistidas ao acionar SALVAR. A remoção de um apontamento (decisão do
  // PO — Pergunta 2) segue o mesmo modelo.
  // - apontamentosEditadosPendentes: ids com padrão e/ou linhas alterados (valores ficam no próprio
  //   `apontamentos` local; aqui guardamos quais persistir no SALVAR).
  const [apontamentosEditadosPendentes, setApontamentosEditadosPendentes] = useState<Set<number>>(new Set());
  // - remocoesPendentes: ids marcados para remoção (aplicada só no SALVAR).
  const [remocoesPendentes, setRemocoesPendentes] = useState<Set<number>>(new Set());
  // - agrupOpsPendentes: inclusões de agrupamento (componentes/bases) a aplicar no SALVAR.
  const [agrupOpsPendentes, setAgrupOpsPendentes] = useState<AgrupOpPendente[]>([]);
  // Gerador de ids temporários (negativos) para os agrupados inseridos otimisticamente.
  const tempIdRef = useRef<number>(-1);
  
  // Ref para scroll automático ao adicionar novo item
  const lastItemRef = useRef<HTMLDivElement>(null);

  // Função para construir URL da imagem do componente
  const buildImageSrc = (icone?: string | null): string => {
    if (!icone) return "";
    const trimmed = icone.trim();
    if (!trimmed) return "";
    
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      return trimmed;
    }
    
    if (trimmed.startsWith("/")) {
      return `${process.env.REACT_APP_API_URL || "http://localhost:3099"}${trimmed}`;
    }
    
    return `${process.env.REACT_APP_API_URL || "http://localhost:3099"}/${trimmed}`;
  };

  useEffect(() => {
    carregarLinhasSistema();
  }, []);

  useEffect(() => {
    if (empresaId && componenteBaseId) {
      carregarDados();
    }
  }, [empresaId, componenteBaseId]);

  const carregarDados = async () => {
    if (!empresaId || !componenteBaseId) return;

    setIsLoading(true);
    try {
      const idEmpresa = parseInt(empresaId);
      const idComponenteBase = parseInt(componenteBaseId);

      await carregarPlanilhaPendente(idEmpresa);

      const stateData = (location.state as any)?.codigoBase;
      if (stateData && stateData.idComponenteBase === idComponenteBase) {
        setCodigoBaseSelecionado(stateData);
        await carregarApontamentos(stateData);
      } else {
        const response = await listarCodigosBase({
          idEmpresa,
          page: 1,
          limit: 1000,
        });

        const componente = response.data?.items?.find(
          (c: ICodigoBase) => c.idComponenteBase === idComponenteBase
        );

        if (componente) {
          setCodigoBaseSelecionado(componente);
          await carregarApontamentos(componente);
        } else {
          openNotification({ message: "Componente não encontrado", type: "error" });
          navigate(-1);
        }
      }
    } catch (error) {
      openNotification({ message: "Erro ao carregar dados", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // Plano 14/10/2026 (Frente 6): mesma regra do backend (IHistoricoImportacaoRepository.ExisteImportacaoPendente).
  const carregarPlanilhaPendente = async (idEmpresa: number) => {
    const ultima = await obterUltimaImportacao(idEmpresa);
    setPlanilhaPendente(
      !!ultima &&
        ultima.status !== STATUS_IMPORTACAO_DISPONIVEL_MERCADO &&
        ultima.status !== STATUS_IMPORTACAO_REMOVIDA
    );
  };

  // Plano 14/10/2026 (Frente 6): barreira dos handlers de edição (os controles já ficam desabilitados).
  const bloqueadoPorPlanilha = (): boolean => {
    if (!planilhaPendente) return false;
    openNotification({ message: MENSAGEM_PLANILHA_PENDENTE, type: "warning" });
    return true;
  };

  const carregarApontamentos = async (codigoBase: ICodigoBase) => {
    if (!empresaId) return;

    try {
      const response = await obterApontamentos(
        parseInt(empresaId),
        codigoBase.idComponenteBase
      );
      setApontamentos(response.data);
    } catch (error) {
      openNotification({ message: "Erro ao carregar apontamentos", type: "error" });
    }
  };

  const carregarLinhasSistema = async () => {
    try {
      const idEmpresa = empresaId ? parseInt(empresaId) : undefined;
      const linhas = await listarLinhasSistema(undefined, idEmpresa);
      setLinhasSistemaOptions(linhas);
    } catch (error) {
      console.error("Erro ao carregar linhas/sistemas:", error);
    }
  };

  const handleBuscarCodigoAplicavel = async (itemId: string, codigoBusca: string) => {
    if (!empresaId || !codigoBaseSelecionado) return;
    
    const codigoLimpo = codigoBusca.trim().toUpperCase();
    if (!codigoLimpo) return;

    setIsLoading(true);
    try {
      const response = await listarComponentesAplicaveis(
        parseInt(empresaId),
        codigoBaseSelecionado.idComponenteBase,
        codigoLimpo
      );

      const componenteEncontrado = (response.data.items || []).find(
        (c: IComponenteAplicavel) => c.codigo.toUpperCase() === codigoLimpo
      );

      if (componenteEncontrado) {
        // TAREFA 8.2 (FASE 03): staging — apenas preenche o cartão; nada é persistido aqui.
        // A gravação ocorre somente no botão SALVAR (handleSalvarApontamentos).
        setItensEmEdicao((prev) =>
          prev.map((i) =>
            i.id === itemId
              ? { ...i, componenteAplicavel: componenteEncontrado, isDuplicado: false }
              : i
          )
        );
        setCodigoAplicavelInput((prev) => ({ ...prev, [itemId]: "" }));
      } else {
        openNotification({ message: "Código não encontrado ou não aplicável", type: "warning" });
      }
    } catch (error) {
      openNotification({ message: extractApiError(error, "Erro ao buscar código aplicável"), type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // FASE 03 (item 6): persiste os itens em staging via SALVAR. A validação de duplicidade ocorre
  // SOMENTE aqui (no clique em SALVAR), nunca na carga da tela. Identidade = COMPOSIÇÃO COMPLETA:
  // como os itens em staging são SIMPLES (sem agrupamentos), eles só são duplicados de um apontamento
  // salvo que também seja SIMPLES (mesmo código aplicável e sem agrupamentos). Um agrupamento de
  // mesmo par tem composição diferente e NÃO bloqueia. O backend revalida de forma autoritativa.
  // Plano 05/10/2026 (Frente 7): detecção de duplicados extraída para uma função pura (sem efeitos).
  // Devolve os ids de componente aplicável dos itens em staging que são idênticos a um apontamento
  // salvo simples ativo ou que se repetem entre os próprios pendentes.
  const detectarDuplicados = (): Set<number> => {
    const pendentes = itensEmEdicao.filter((i) => i.componenteAplicavel);

    // Ids de aplicáveis de apontamentos SALVOS ativos e SIMPLES (sem agrupamentos de aplicáveis
    // nem de bases) — só estes colidem com um item simples em staging.
    const idsSalvosSimplesAtivos = new Set(
      apontamentos
        .filter((a) => !a.excluida)
        .filter((a) =>
          (!a.componentesAgrupados || a.componentesAgrupados.length === 0) &&
          (!a.componentesBaseAgrupados || a.componentesBaseAgrupados.length === 0)
        )
        .map((a) => a.idComponenteAplicavel)
    );

    // Detecta duplicados: contra salvos simples ativos e entre os próprios pendentes (mesmo idComponente).
    const ocorrencias = new Map<number, number>();
    pendentes.forEach((i) => {
      const id = i.componenteAplicavel!.idComponente;
      ocorrencias.set(id, (ocorrencias.get(id) || 0) + 1);
    });

    const idsDuplicados = new Set<number>();
    pendentes.forEach((i) => {
      const id = i.componenteAplicavel!.idComponente;
      if (idsSalvosSimplesAtivos.has(id) || (ocorrencias.get(id) || 0) > 1) {
        idsDuplicados.add(id);
      }
    });

    return idsDuplicados;
  };

  // Plano 05/10/2026 (Frente 7): marca em vermelho (isDuplicado) só os itens cujo aplicável está no conjunto.
  const marcarDuplicados = (idsDuplicados: Set<number>) => {
    setItensEmEdicao((prev) =>
      prev.map((i) =>
        i.componenteAplicavel && idsDuplicados.has(i.componenteAplicavel.idComponente)
          ? { ...i, isDuplicado: true }
          : { ...i, isDuplicado: false }
      )
    );
  };

  const handleSalvarApontamentos = async () => {
    if (!empresaId || !componenteBaseId) return;
    if (bloqueadoPorPlanilha()) return;

    const pendentes = itensEmEdicao.filter((i) => i.componenteAplicavel);
    // FASE 03 (itens 4/5/7 — 22-06): o SALVAR também grava edições em apontamentos já salvos
    // (padrão/linhas), aplica os agrupamentos enfileirados e executa as remoções pendentes.
    const temEdicoesSalvas = apontamentosEditadosPendentes.size > 0;
    const temAgrupOps = agrupOpsPendentes.length > 0;
    const temRemocoes = remocoesPendentes.size > 0;
    if (pendentes.length === 0 && !temEdicoesSalvas && !temAgrupOps && !temRemocoes) return;

    // Plano 05/10/2026 (Frente 7, P4): os duplicados NÃO abortam mais a gravação — ficam em vermelho,
    // não são enviados ao backend e os demais itens são gravados. Sem duplicados, o fluxo é o de sempre.
    const idsDuplicados = detectarDuplicados();
    const aGravar = pendentes.filter((i) => !idsDuplicados.has(i.componenteAplicavel!.idComponente));
    if (idsDuplicados.size > 0) marcarDuplicados(idsDuplicados);

    if (aGravar.length === 0 && !temEdicoesSalvas && !temAgrupOps && !temRemocoes) {
      openNotification({
        message:
          "Nenhum item foi gravado: todos os apontamentos pendentes são idênticos a um já existente (destacados em vermelho).",
        type: "warning",
      });
      return;
    }

    // Persiste item a item (somente os não duplicados).
    setIsSalvandoLote(true);
    const idsComErro = new Set<number>();
    const mensagensErro: string[] = [];
    try {
      for (const item of aGravar) {
        try {
          await salvarApontamento({
            idEmpresa: parseInt(empresaId),
            idComponenteBase: parseInt(componenteBaseId),
            idComponenteAplicavel: item.componenteAplicavel!.idComponente,
            isPadrao: item.isPadrao || false,
            linhasSistema: item.linhasSistema || [],
          });
        } catch (error) {
          idsComErro.add(item.componenteAplicavel!.idComponente);
          mensagensErro.push(extractApiError(error, `Erro ao salvar ${item.componenteAplicavel!.codigo}`));
        }
      }

      // Mantém destacados apenas os itens que falharam ou são duplicados; remove os que gravaram.
      setItensEmEdicao((prev) =>
        prev
          .filter(
            (i) =>
              !i.componenteAplicavel ||
              idsComErro.has(i.componenteAplicavel.idComponente) ||
              idsDuplicados.has(i.componenteAplicavel.idComponente)
          )
          .map((i) =>
            i.componenteAplicavel &&
            (idsComErro.has(i.componenteAplicavel.idComponente) ||
              idsDuplicados.has(i.componenteAplicavel.idComponente))
              ? { ...i, isDuplicado: true }
              : i
          )
      );

      // FASE 03 (itens 5 — 22-06): aplica os AGRUPAMENTOS enfileirados (somente de apontamentos que
      // NÃO serão removidos). Cada inclusão rebaixa a aplicabilidade para "Em Edição" no backend.
      for (const op of agrupOpsPendentes) {
        if (remocoesPendentes.has(op.idAplicabilidade)) continue;
        try {
          if (op.tipo === "addComponente") {
            await adicionarComponenteAgrupado(op.idAplicabilidade, op.idComponente);
          } else {
            await adicionarComponenteBaseAgrupado(op.idAplicabilidade, op.idComponenteBase);
          }
        } catch (error) {
          mensagensErro.push(extractApiError(error, "Erro ao aplicar agrupamento"));
        }
      }

      // FASE 03 (itens 4/7 — 22-06): grava as edições (padrão/linhas) dos apontamentos já salvos
      // (exceto os marcados para remoção). O backend rebaixa para "Em Edição" e desmarca o padrão
      // anterior quando aplicável.
      for (const idAplicabilidade of apontamentosEditadosPendentes) {
        if (remocoesPendentes.has(idAplicabilidade)) continue;
        const apt = apontamentos.find((a) => a.idAplicabilidade === idAplicabilidade);
        if (!apt) continue;
        try {
          await salvarApontamento({
            idAplicabilidade: apt.idAplicabilidade,
            idEmpresa: parseInt(empresaId),
            idComponenteBase: apt.idComponenteBase,
            idComponenteAplicavel: apt.idComponenteAplicavel,
            isPadrao: apt.isPadrao,
            linhasSistema: apt.linhasSistema || [],
          });
        } catch (error) {
          mensagensErro.push(extractApiError(error, "Erro ao salvar a edição da aplicabilidade"));
        }
      }

      // FASE 03 (item 4 / Pergunta 2 do PO — 22-06): executa as REMOÇÕES pendentes (por último).
      for (const idAplicabilidade of remocoesPendentes) {
        try {
          await removerApontamento(idAplicabilidade);
        } catch (error) {
          mensagensErro.push(extractApiError(error, "Erro ao remover apontamento"));
        }
      }

      // Limpa as pendências aplicadas (sucessos e tentativas) — o reload abaixo traz o estado real.
      setApontamentosEditadosPendentes(new Set());
      setRemocoesPendentes(new Set());
      setAgrupOpsPendentes([]);

      if (codigoBaseSelecionado) {
        await carregarApontamentos(codigoBaseSelecionado);
      }

      if (idsComErro.size > 0 || mensagensErro.length > 0) {
        openNotification({ message: mensagensErro[0] || "Alguns itens não puderam ser salvos.", type: "error" });
      } else if (idsDuplicados.size > 0) {
        // Plano 05/10/2026 (Frente 7): gravou os válidos; os duplicados continuam em vermelho na tela.
        openNotification({
          message:
            "Aplicabilidades salvas. Os apontamentos idênticos a um já existente não foram gravados e continuam destacados em vermelho.",
          type: "warning",
        });
      } else {
        openNotification({ message: "Aplicabilidades salvas com sucesso", type: "success" });
      }
    } finally {
      setIsSalvandoLote(false);
    }
  };

  // TAREFA 8.7 (FASE 03): confirmação antes de gravar os pendentes.
  const handleConfirmarSalvar = () => {
    setConfirmarSalvarOpen(false);
    void handleSalvarApontamentos();
  };

  // Frente 9 (02/10/2026, decisão P2): o padrão é OPCIONAL (o CEM Desktop importa sem o dado),
  // mas a tela avisa. Se NENHUM apontamento salvo ativo (não excluído e não marcado para remoção)
  // nem NENHUM cartão em edição está como padrão, pede confirmação antes da confirmação normal
  // de salvar. CANCELAR volta à edição sem gravar; CONTINUAR segue o fluxo normal.
  const existePadraoNaEdicao = () =>
    apontamentos.some((a) => !a.excluida && !remocoesPendentes.has(a.idAplicabilidade) && a.isPadrao) ||
    itensEmEdicao.some((i) => i.isPadrao);

  // Plano 05/10/2026 (Frente 7, P4): o modal "Salvar aplicabilidades" só aparece quando há apontamento
  // idêntico a um já existente; sem duplicados, grava direto.
  const prosseguirSalvar = () => {
    const idsDuplicados = detectarDuplicados();
    if (idsDuplicados.size === 0) {
      void handleSalvarApontamentos();
      return;
    }
    marcarDuplicados(idsDuplicados);
    setConfirmarSalvarOpen(true);
  };

  const handleClickSalvar = () => {
    if (bloqueadoPorPlanilha()) return;
    if (!existePadraoNaEdicao()) {
      setConfirmarSemPadraoOpen(true);
      return;
    }
    prosseguirSalvar();
  };

  const handleContinuarSemPadrao = () => {
    setConfirmarSemPadraoOpen(false);
    prosseguirSalvar();
  };


  const handleRemoverItemEmEdicao = (itemId: string) => {
    setItensEmEdicao((prev) => prev.filter((i) => i.id !== itemId));
  };

  // FASE 03 (itens 3/4): "auto-salvar e agrupar". Ao clicar em Agrupar (Componentes ou Bases) num
  // cartão AINDA NÃO salvo, persiste aquele apontamento (gerando idAplicabilidade) e abre o
  // agrupamento já apontando para ELE — corrige o agrupamento ir para o apontamento errado.
  const handleAgruparDeItemEmEdicao = async (
    itemEmEdicao: {
      id: string;
      componenteAplicavel?: IComponenteAplicavel;
      isPadrao: boolean;
      linhasSistema: ILinhaSistemaPadrao[];
    },
    isAgrupamentoBases: boolean
  ) => {
    if (!empresaId || !componenteBaseId) return;
    if (bloqueadoPorPlanilha()) return;
    if (!itemEmEdicao.componenteAplicavel) {
      openNotification({ message: "Selecione o código aplicável antes de agrupar.", type: "warning" });
      return;
    }

    setIsLoading(true);
    try {
      await salvarApontamento({
        idEmpresa: parseInt(empresaId),
        idComponenteBase: parseInt(componenteBaseId),
        idComponenteAplicavel: itemEmEdicao.componenteAplicavel.idComponente,
        isPadrao: itemEmEdicao.isPadrao || false,
        linhasSistema: itemEmEdicao.linhasSistema || [],
      });

      // Recarrega e localiza o apontamento recém-criado (maior idAplicabilidade do mesmo aplicável).
      const resp = await obterApontamentos(parseInt(empresaId), parseInt(componenteBaseId));
      setApontamentos(resp.data);
      const idAplic = itemEmEdicao.componenteAplicavel.idComponente;
      const novo = (resp.data || [])
        .filter((a) => a.idComponenteAplicavel === idAplic && !a.excluida)
        .sort((a, b) => b.idAplicabilidade - a.idAplicabilidade)[0];

      // Remove o cartão de staging (já persistido).
      setItensEmEdicao((prev) => prev.filter((i) => i.id !== itemEmEdicao.id));

      if (novo) {
        if (isAgrupamentoBases) {
          handleAbrirModalAgrupamentoBases(novo.idAplicabilidade);
        } else {
          handleAbrirModalAgrupamento(novo.idAplicabilidade, false);
        }
      }
    } catch (error) {
      openNotification({ message: extractApiError(error, "Erro ao salvar a aplicabilidade para agrupar"), type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // FASE 03 (item 4 / Pergunta 2 do PO — 22-06): remover um apontamento NÃO remove na hora. Marca/
  // desmarca a remoção como pendente (refletida na tela) e habilita o SALVAR; a remoção efetiva
  // ocorre apenas ao acionar SALVAR, junto das demais edições.
  const handleToggleRemocaoPendente = (idAplicabilidade: number) => {
    if (bloqueadoPorPlanilha()) return;
    setRemocoesPendentes((prev) => {
      const novo = new Set(prev);
      if (novo.has(idAplicabilidade)) {
        novo.delete(idAplicabilidade);
      } else {
        novo.add(idAplicabilidade);
      }
      return novo;
    });
  };

  const handleAbrirModalAgrupamento = (idAplicabilidade: number, isAgrupamentoBases: boolean = false) => {
    if (bloqueadoPorPlanilha()) return;
    setModalApontamento({
      isOpen: true,
      isAgrupamento: true,
      isAgrupamentoBases: isAgrupamentoBases, // true = agrupar bases, false = agrupar aplicáveis
      idAplicabilidadeParaAgrupar: idAplicabilidade,
    });
  };

  // FASE 03 (item 5 — 22-06): agrupar COMPONENTES não persiste na hora. Insere o agrupado na tela
  // (otimista, com id temporário), rebaixa a situação local para "Em Edição" e enfileira a operação
  // para ser aplicada no SALVAR.
  // Frente 10 (02/10/2026): recebe VÁRIOS componentes de uma vez (multi-seleção do modal, igual ao
  // Agrupar Bases) e enfileira UMA operação addComponente por item — o backend segue aceitando
  // 1 item por chamada.
  const handleAdicionarComponenteAgrupado = (componentes: IComponenteAplicavel[]) => {
    const idAplic = modalApontamento.idAplicabilidadeParaAgrupar;
    if (!idAplic || componentes.length === 0) return;

    const novosAgrupados: IComponenteAgrupado[] = componentes.map((componente) => ({
      idAplicabilidadeAgrupamento: tempIdRef.current--,
      idComponenteAplicavel: componente.idComponente,
      codigoAplicavel: componente.codigo,
      descricaoAplicavel: componente.descricao,
      iconeAplicavel: componente.icone,
      corAplicavel: componente.cor,
      referenciaAplicavel: componente.referencia,
      fichaTecnicaAplicavel: componente.fichaTecnica,
    }));

    setApontamentos((prev) =>
      prev.map((a) =>
        a.idAplicabilidade === idAplic
          ? {
              ...a,
              componentesAgrupados: [...(a.componentesAgrupados || []), ...novosAgrupados],
              situacao: 0, // Em Edição (feedback local; o rebaixamento real ocorre no SALVAR)
            }
          : a
      )
    );
    setAgrupOpsPendentes((prev) => [
      ...prev,
      ...componentes.map((componente) => ({
        tipo: "addComponente" as const,
        idAplicabilidade: idAplic,
        idComponente: componente.idComponente,
      })),
    ]);
    setModalApontamento({ isOpen: false });
    openNotification({
      message: `${componentes.length} componente(s) adicionado(s). Clique em SALVAR para confirmar.`,
      type: "info",
    });
  };

  const handleRemoverComponenteAgrupado = async (idAplicabilidadeAgrupamento: number) => {
    if (bloqueadoPorPlanilha()) return;
    if (!window.confirm("Deseja realmente remover este componente do agrupamento?")) return;

    setIsLoading(true);
    try {
      await removerComponenteAgrupado(idAplicabilidadeAgrupamento);
      openNotification({ message: "Componente removido do agrupamento", type: "success" });
      if (codigoBaseSelecionado) {
        await carregarApontamentos(codigoBaseSelecionado);
      }
    } catch (error: any) {
      openNotification({
        message: error?.response?.data?.errors?.[0] || "Erro ao remover componente agrupado",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAbrirModalAgrupamentoBases = (idAplicabilidade?: number) => {
    if (bloqueadoPorPlanilha()) return;
    setModalAgrupamentoBases({
      isOpen: true,
      idAplicabilidadeParaAgrupar: idAplicabilidade,
    });
  };

  // FASE 03 (item 5 — 22-06): agrupar BASES não persiste na hora. Insere as bases na tela (otimista,
  // ids temporários), rebaixa a situação local para "Em Edição" e enfileira as operações para o SALVAR.
  const handleAdicionarBasesAgrupadas = (codigosBaseSelecionados: ICodigoBase[]) => {
    const idAplic = modalAgrupamentoBases.idAplicabilidadeParaAgrupar;
    if (!idAplic) return;

    const novasBases: IComponenteBaseAgrupado[] = codigosBaseSelecionados.map((cb) => ({
      idAplicabilidadeAgrupamentoBase: tempIdRef.current--,
      idComponenteBase: cb.idComponenteBase,
      codigoBase: cb.codigoBase,
      descricaoBase: cb.descricao,
      iconeBase: cb.icone,
      corBase: cb.cor,
      referenciaBase: cb.referencia,
      fichaTecnicaBase: cb.fichaTecnica,
    }));

    setApontamentos((prev) =>
      prev.map((a) =>
        a.idAplicabilidade === idAplic
          ? {
              ...a,
              componentesBaseAgrupados: [...(a.componentesBaseAgrupados || []), ...novasBases],
              situacao: 0, // Em Edição (feedback local; o rebaixamento real ocorre no SALVAR)
            }
          : a
      )
    );
    setAgrupOpsPendentes((prev) => [
      ...prev,
      ...codigosBaseSelecionados.map((cb) => ({
        tipo: "addBase" as const,
        idAplicabilidade: idAplic,
        idComponenteBase: cb.idComponenteBase,
      })),
    ]);
    setModalAgrupamentoBases({ isOpen: false });
    openNotification({
      message: `${codigosBaseSelecionados.length} código(s) base adicionado(s). Clique em SALVAR para confirmar.`,
      type: "info",
    });
  };

  const handleRemoverBaseAgrupada = async (idAplicabilidadeAgrupamentoBase: number) => {
    if (bloqueadoPorPlanilha()) return;
    if (!window.confirm("Deseja realmente remover este código base do agrupamento?")) return;

    setIsLoading(true);
    try {
      await removerComponenteBaseAgrupado(idAplicabilidadeAgrupamentoBase);
      openNotification({ message: "Código base removido do agrupamento", type: "success" });
      if (codigoBaseSelecionado) {
        await carregarApontamentos(codigoBaseSelecionado);
      }
    } catch (error: any) {
      openNotification({
        message: error?.response?.data?.errors?.[0] || "Erro ao remover código base do agrupamento",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // FASE 03 (item 7 — 22-06): trocar o Padrão NÃO grava mais sozinho. Atualiza o estado local
  // (feedback) e marca o apontamento como pendente; a gravação (e o rebaixamento para "Em Edição")
  // ocorre apenas no SALVAR. O backend, ao salvar o que ganhou o padrão, desmarca/rebaixa os demais.
  const handleTogglePadrao = (apontamento: IApontamento, novoValor: boolean) => {
    // TAREFA 11.7 (FASE 03): apontamento excluído (tombstone) não é editável.
    if (apontamento.excluida) return;
    if (bloqueadoPorPlanilha()) return;

    if (novoValor) {
      // Ativando: desativa todos os outros (local) e marca como editado.
      setApontamentos((prev) =>
        prev.map((a) => ({ ...a, isPadrao: a.idAplicabilidade === apontamento.idAplicabilidade }))
      );
      setItensEmEdicao((prev) => prev.map((i) => ({ ...i, isPadrao: false })));
    } else {
      // Desativando: apenas o próprio. (Frente 9, 02/10/2026: pode desligar o único padrão —
      // o padrão é opcional; a tela só confirma no SALVAR.)
      setApontamentos((prev) =>
        prev.map((a) =>
          a.idAplicabilidade === apontamento.idAplicabilidade ? { ...a, isPadrao: false } : a
        )
      );
    }
    setApontamentosEditadosPendentes((prev) => new Set(prev).add(apontamento.idAplicabilidade));
  };

  // FASE 03 (item 4 — 22-06): altera as linhas/sistemas de um apontamento salvo SEM gravar — apenas
  // atualiza o estado local e marca pendência. A gravação ocorre no SALVAR.
  const handleAlterarLinhasSistema = (apontamento: IApontamento, novasLinhas: ILinhaSistemaPadrao[]) => {
    if (apontamento.excluida) return;
    if (bloqueadoPorPlanilha()) return;
    setApontamentos((prev) =>
      prev.map((a) =>
        a.idAplicabilidade === apontamento.idAplicabilidade ? { ...a, linhasSistema: novasLinhas } : a
      )
    );
    setApontamentosEditadosPendentes((prev) => new Set(prev).add(apontamento.idAplicabilidade));
  };

  if (!codigoBaseSelecionado) {
    return (
      <DashboardStackCustom>
        <BackdropLoading isOpen={isLoading} />
      </DashboardStackCustom>
    );
  }

  return (
    <DashboardStackCustom sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: "calc(100vh - 64px)" }}>
      <BackdropLoading isOpen={isLoading} />

      <NavigationHeader
        title="CADASTRO DE APLICABILIDADES"
        label="Home > Aplicabilidade"
        hasBackButton={true}
        goBack={() => navigate(-1)}
      />

      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          bgcolor: "white",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        {/* Header com informações do componente base */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: "white",
            borderRadius: 1,
            display: "flex",
            gap: 3,
            alignItems: "stretch",
          }}
        >
          {/* Imagem do componente */}
          <Box
            sx={{
              width: 120,
              height: 120,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "white",
              borderRadius: 1,
              border: "1px solid #e0e0e0",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            {codigoBaseSelecionado.icone ? (
              <img
                src={buildImageSrc(codigoBaseSelecionado.icone)}
                alt={codigoBaseSelecionado.codigoBase}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <Box sx={{ width: "100%", height: "100%", bgcolor: "#f5f5f5" }} />
            )}
          </Box>

          {/* Informações do componente */}
          <Box sx={{ flex: 1, display: "flex" }}>
            {/* Coluna 1: Código e Grupo */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mr: 6 }}>
              <Box>
                <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 600, display: "block", mb: 0.5 }}>
                  Código
                </Typography>
                <Typography variant="body2" sx={{ color: "#000" }}>
                  {codigoBaseSelecionado.codigoBase}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 600, display: "block", mb: 0.5 }}>
                  Grupo
                </Typography>
                <Typography variant="body2" sx={{ color: "#000" }}>
                  {codigoBaseSelecionado.grupo}
                </Typography>
              </Box>
            </Box>

            {/* Coluna 2: Descrição e Status */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: "500px", mr: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 600, display: "block", mb: 0.5 }}>
                  Descrição
                </Typography>
                <Typography variant="body2" sx={{ color: "#000" }}>
                  {codigoBaseSelecionado.descricao}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 600, display: "block", mb: 0.5 }}>
                  Status
                </Typography>
                <Typography variant="body2" sx={{ color: "#000" }}>
                  ATIVO
                </Typography>
              </Box>
            </Box>

            {/* Coluna 3: Ficha Técnica */}
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
              <Box>
                <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 600, display: "block", mb: 0.5 }}>
                  Ficha Técnica
                </Typography>
                {codigoBaseSelecionado.fichaTecnica ? (
                  <Tooltip title="Visualizar ficha técnica" arrow>
                    <IconButton
                      size="small"
                      onClick={() => window.open(codigoBaseSelecionado.fichaTecnica, "_blank", "noopener,noreferrer")}
                      sx={{ p: 0 }}
                    >
                      <DescriptionIcon fontSize="small" sx={{ color: "primary.main" }} />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Typography variant="caption" sx={{ color: "text.disabled" }}>—</Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* Referência */}
          <Box sx={{ 
            flexShrink: 0, 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            alignSelf: "stretch",
            width: "200px",
          }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "primary.main",
                textTransform: "uppercase",
                fontSize: "1.5rem",
                textAlign: "center",
              }}
            >
              {codigoBaseSelecionado.referencia || "MERCADO"}
            </Typography>
          </Box>
        </Box>

        {/* Borda abaixo do header */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }} />

        {/* Plano 14/10/2026 (Frente 6): planilha em andamento → somente leitura. */}
        {planilhaPendente && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {MENSAGEM_PLANILHA_PENDENTE}
          </Alert>
        )}

        {/* Botões no canto direito */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 2 }}>
          {/* TAREFA 8.5 (FASE 03): SALVAR persiste os itens em staging (com confirmação). */}
          <Button
            variant="contained"
            color="success"
            disabled={
              !(
                itensEmEdicao.some((i) => i.componenteAplicavel) ||
                apontamentosEditadosPendentes.size > 0 ||
                agrupOpsPendentes.length > 0 ||
                remocoesPendentes.size > 0
              ) || isSalvandoLote || planilhaPendente
            }
            onClick={handleClickSalvar}
          >
            SALVAR
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={planilhaPendente}
            onClick={() => {
              // Adicionar um novo item vazio na lista
              const novoId = `temp-${Date.now()}`;
              setItensEmEdicao((prev) => [...prev, {
                id: novoId,
                isPadrao: false,
                linhasSistema: [],
              }]);

              // Scroll automático para o novo item
              setTimeout(() => {
                lastItemRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
              }, 100);
            }}
          >
            ADICIONAR APLICABILIDADE
          </Button>
        </Box>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
          
          {(apontamentos.length > 0 || itensEmEdicao.length > 0) ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
              {/* Itens em edição (novos itens ainda não salvos) */}
              {itensEmEdicao.map((itemEmEdicao, index) => {
                const componenteAplicavel = itemEmEdicao.componenteAplicavel;
                return (
                  <Box
                    key={itemEmEdicao.id}
                    ref={index === itensEmEdicao.length - 1 ? lastItemRef : null}
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                      position: "relative",
                      mb: 3,
                    }}
                  >
                    {/* Número do item - círculo cinza à esquerda */}
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        bgcolor: "#9e9e9e",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        flexShrink: 0,
                        zIndex: 1,
                      }}
                    >
                      {apontamentos.length + index + 1}
                    </Box>

                    {/* Wrapper para o container principal + toggle Padrão */}
                    <Box sx={{ flex: 1, position: "relative" }}>
                      {/* Toggle Padrão - canto superior direito */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          zIndex: 2,
                          display: "flex",
                          alignItems: "center",
                          bgcolor: "white",
                          borderRadius: 1,
                          px: 1,
                        }}
                      >
                        <Switch
                          checked={itemEmEdicao.isPadrao}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const novoValor = e.target.checked;
                            if (novoValor) {
                              // Se ativando, desativa todos os outros (itens em edição e apontamentos salvos)
                              setItensEmEdicao((prev) =>
                                prev.map((i) => ({ ...i, isPadrao: i.id === itemEmEdicao.id }))
                              );
                              setApontamentos((prev) =>
                                prev.map((a) => ({ ...a, isPadrao: false }))
                              );
                            } else {
                              // Se desativando, apenas desativa o próprio
                              setItensEmEdicao((prev) =>
                                prev.map((i) =>
                                  i.id === itemEmEdicao.id ? { ...i, isPadrao: false } : i
                                )
                              );
                            }
                          }}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "primary.main",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "primary.main",
                            },
                          }}
                        />
                        <Typography variant="body2" sx={{ color: "#666", fontSize: "0.875rem" }}>
                          Padrão
                        </Typography>
                      </Box>

                      {/* Container principal com borda contínua */}
                      {/* TAREFA 8.6 (FASE 03): borda vermelha quando o item em staging é duplicado. */}
                      <Box
                        sx={{
                          border: itemEmEdicao.isDuplicado ? "1px solid #d32f2f" : "1px solid #4db6ac",
                          borderRadius: 1,
                          p: 2,
                          pt: 6,
                          bgcolor: "white",
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                      {itemEmEdicao.isDuplicado && (
                        <Typography variant="caption" sx={{ color: "#d32f2f" }}>
                          Aplicabilidade idêntica já existente
                        </Typography>
                      )}
                      {/* Seção superior: DOIS containers lado a lado */}
                      <Box sx={{ display: "flex", gap: 2, minHeight: "120px", alignItems: "flex-start" }}>
                        {/* Container ESQUERDO: Informações do componente BASE */}
                        <Box
                          sx={{
                            flex: 1,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            p: 1.5,
                            bgcolor: "#fafafa",
                            display: "flex",
                            flexDirection: "column",
                            alignSelf: "flex-start",
                          }}
                        >
                          <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
                            {/* Imagem do componente BASE */}
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "white",
                                borderRadius: 1,
                                border: "1px solid #e0e0e0",
                                overflow: "hidden",
                                flexShrink: 0,
                              }}
                            >
                              {codigoBaseSelecionado.icone ? (
                                <img
                                  src={buildImageSrc(codigoBaseSelecionado.icone)}
                                  alt={codigoBaseSelecionado.codigoBase}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                  }}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = "none";
                                  }}
                                />
                              ) : (
                                <Box sx={{ width: "100%", height: "100%", bgcolor: "#f5f5f5" }} />
                              )}
                            </Box>

                            {/* Informações do componente BASE */}
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                                  {codigoBaseSelecionado.codigoBase}
                                </Typography>
                                {codigoBaseSelecionado.fichaTecnica && (
                                  <Tooltip title="Visualizar ficha técnica" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(codigoBaseSelecionado.fichaTecnica, "_blank", "noopener,noreferrer");
                                      }}
                                      sx={{ p: 0 }}
                                    >
                                      <DescriptionIcon fontSize="small" sx={{ color: "primary.main" }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                              <Typography variant="caption" sx={{ color: "primary.main", display: "block", mb: 0.3, fontSize: "0.75rem" }}>
                                {codigoBaseSelecionado.descricao}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "primary.main", display: "block", mb: 0.3 }}>
                                COR: {codigoBaseSelecionado.cor?.toUpperCase() || "N/A"}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "primary.main", display: "block" }}>
                                {codigoBaseSelecionado.referencia || "MERCADO"}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Botão no canto inferior esquerdo - Agrupar CÓDIGOS BASE (N Bases → 1 Aplicável) */}
                          <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", pr: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleAgruparDeItemEmEdicao(itemEmEdicao, true)}
                              disabled={!componenteAplicavel}
                              sx={{
                                fontSize: "0.65rem",
                                py: 0.5,
                                px: 1,
                                borderColor: "#e0e0e0",
                                color: !componenteAplicavel ? "#999" : "#666",
                                textTransform: "none",
                              }}
                            >
                              + AGRUPAR BASES
                            </Button>
                          </Box>
                        </Box>

                        {/* Container DIREITO: Input para código aplicável OU informações se já selecionado */}
                        <Box
                          sx={{
                            flex: 1,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                            p: 1.5,
                            bgcolor: "#fafafa",
                            display: "flex",
                            flexDirection: "column",
                            alignSelf: "flex-start",
                          }}
                        >
                          {componenteAplicavel ? (
                            <>
                              <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
                                {/* Imagem do componente APLICÁVEL */}
                                <Box
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: "white",
                                    borderRadius: 1,
                                    border: "1px solid #e0e0e0",
                                    overflow: "hidden",
                                    flexShrink: 0,
                                  }}
                                >
                                  {componenteAplicavel.icone ? (
                                    <img
                                      src={buildImageSrc(componenteAplicavel.icone)}
                                      alt={componenteAplicavel.codigo}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                      }}
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = "none";
                                      }}
                                    />
                                  ) : (
                                    <Box sx={{ width: "100%", height: "100%", bgcolor: "#f5f5f5" }} />
                                  )}
                                </Box>

                                {/* Informações do componente APLICÁVEL */}
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                                      {componenteAplicavel.codigo}
                                    </Typography>
                                    {componenteAplicavel.fichaTecnica && (
                                      <Tooltip title="Visualizar ficha técnica" arrow>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(componenteAplicavel.fichaTecnica, "_blank", "noopener,noreferrer");
                                          }}
                                          sx={{ p: 0 }}
                                        >
                                          <DescriptionIcon fontSize="small" sx={{ color: "primary.main" }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                  <Typography variant="caption" sx={{ color: "primary.main", display: "block", mb: 0.3, fontSize: "0.75rem" }}>
                                    {componenteAplicavel.descricao}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "primary.main", display: "block", mb: 0.3 }}>
                                    COR: {componenteAplicavel.cor?.toUpperCase() || "N/A"}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "primary.main", display: "block" }}>
                                    {codigoBaseSelecionado.referencia || "MERCADO"}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Botão no canto inferior direito — FASE 03 (item 3): habilitado.
                                  Auto-salva o apontamento e abre o agrupamento de componentes. */}
                              <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", pr: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleAgruparDeItemEmEdicao(itemEmEdicao, false)}
                                  sx={{
                                    fontSize: "0.65rem",
                                    py: 0.5,
                                    px: 1,
                                    borderColor: "#e0e0e0",
                                    color: "#666",
                                    textTransform: "none",
                                  }}
                                >
                                  + AGRUPAR COMPONENTES
                                </Button>
                              </Box>
                            </>
                          ) : (
                            <>
                              <TextField
                                fullWidth
                                label="Código do componente aplicável"
                                placeholder="Digite o código e pressione Enter"
                                variant="outlined"
                                size="small"
                                value={codigoAplicavelInput[itemEmEdicao.id] || ""}
                                onChange={(e) => {
                                  setCodigoAplicavelInput((prev) => ({
                                    ...prev,
                                    [itemEmEdicao.id]: e.target.value,
                                  }));
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleBuscarCodigoAplicavel(
                                      itemEmEdicao.id,
                                      codigoAplicavelInput[itemEmEdicao.id] || ""
                                    );
                                  }
                                }}
                                sx={{
                                  mb: 1,
                                  "& .MuiOutlinedInput-root": {
                                    bgcolor: "white",
                                  },
                                }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        edge="end"
                                        onClick={() => {
                                          setModalApontamento({ isOpen: true, itemEmEdicaoId: itemEmEdicao.id });
                                        }}
                                        sx={{ p: 0.5 }}
                                      >
                                        <ListIcon sx={{ color: "#4db6ac" }} />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />

                              {/* Botão no canto inferior direito — FASE 03 (item 3): sem aplicável
                                  selecionado ainda; orienta o usuário em vez de ficar inerte. */}
                              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => openNotification({ message: "Selecione o código aplicável antes de agrupar.", type: "warning" })}
                                  sx={{
                                    fontSize: "0.65rem",
                                    py: 0.5,
                                    px: 1,
                                    borderColor: "#e0e0e0",
                                    color: "#666",
                                    textTransform: "none",
                                  }}
                                >
                                  + AGRUPAR COMPONENTES
                                </Button>
                              </Box>
                            </>
                          )}
                        </Box>
                      </Box>

                      {/* Seção inferior: Campo de linhas/sistemas e Observação */}
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1, color: "primary.main", fontSize: "0.875rem" }}>
                            Defina as linha(s)/sistema(s) para essa aplicabilidade
                          </Typography>
                          <FormControl fullWidth size="small">
                            <ReactSelect
                              isMulti
                              isClearable={false}
                              maxMenuHeight={290}
                              menuPosition="fixed"
                              options={linhasSistemaOptions}
                              value={itemEmEdicao.linhasSistema
                                .map((l) => linhasSistemaOptions.find((o) => o.value === l.idLinhaSistema))
                                .filter((o): o is { value: number; label: string } => Boolean(o))}
                              onChange={(selected) => {
                                const ids = ((selected as Array<{ value: number; label: string }>) || []).map((s) => s.value);
                                setItensEmEdicao((prev) =>
                                  prev.map((i) => {
                                    if (i.id !== itemEmEdicao.id) return i;
                                    // FASE 03 (item 5): toda linha selecionada já é ativa/padrão.
                                    const novasLinhas = ids.map((id) => ({ idLinhaSistema: id, isPadrao: true }));
                                    return { ...i, linhasSistema: novasLinhas };
                                  })
                                );
                              }}
                              placeholder="Selecione linha(s)/sistema(s)"
                              styles={linhasSistemaSelectStyles}
                            />
                          </FormControl>
                          {/* FASE 03 (item 5): seção "Marque a linha/sistema padrão" removida —
                              a seleção da linha já a torna ativa para a aplicabilidade. */}
                        </Box>

                      </Box>
                    </Box>
                    </Box>

                    {/* Botão de remover - fora do box */}
                    <IconButton
                      onClick={() => handleRemoverItemEmEdicao(itemEmEdicao.id)}
                      sx={{
                        bgcolor: "#ffebee",
                        color: "#d32f2f",
                        borderRadius: 1,
                        width: 36,
                        height: 36,
                        flexShrink: 0,
                        "&:hover": {
                          bgcolor: "#ffcdd2",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}

              {/* Apontamentos salvos */}
              {apontamentos.map((apontamento, index) => (
                <Box
                  key={apontamento.idAplicabilidade}
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "center",
                    position: "relative",
                    mb: 3,
                    // FASE 03 (item 4 / Pergunta 2 do PO — 22-06): cartão esmaecido quando marcado para remoção pendente.
                    opacity: remocoesPendentes.has(apontamento.idAplicabilidade) ? 0.5 : 1,
                  }}
                >
                  {/* Número do item - círculo cinza à esquerda */}
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      bgcolor: "#9e9e9e",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 600,
                      fontSize: "1.1rem",
                      flexShrink: 0,
                      zIndex: 1,
                    }}
                  >
                    {index + 1}
                  </Box>

                  {/* Wrapper para o container principal + toggle Padrão */}
                  <Box sx={{ flex: 1, position: "relative" }}>
                    {/* Chip de situação + Toggle Padrão - canto superior direito */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        bgcolor: "white",
                        borderRadius: 1,
                        px: 1,
                      }}
                    >
                      {/* TAREFA 11.7/B7 (FASE 03): chip de situação sempre visível no cartão. */}
                      {typeof apontamento.situacao === "number" && (
                        <Chip
                          size="small"
                          label={SITUACAO_LABEL[apontamento.situacao] || "—"}
                          sx={{
                            bgcolor: SITUACAO_COR[apontamento.situacao] || "#9e9e9e",
                            color: "#fff",
                            fontSize: "0.7rem",
                            height: 22,
                          }}
                        />
                      )}
                      {/* TAREFA 11.7 (FASE 03): tombstone exibe "EXCLUÍDA PELO USUÁRIO" e oculta o Switch Padrão. */}
                      {apontamento.excluida ? (
                        <Chip
                          size="small"
                          label="EXCLUÍDA PELO USUÁRIO"
                          sx={{ bgcolor: "#d32f2f", color: "#fff", fontSize: "0.7rem", height: 22 }}
                        />
                      ) : (
                        <>
                          <Switch
                            checked={apontamento.isPadrao}
                            disabled={planilhaPendente}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleTogglePadrao(apontamento, e.target.checked)}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: "primary.main",
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                backgroundColor: "primary.main",
                              },
                            }}
                          />
                          <Typography variant="body2" sx={{ color: "#666", fontSize: "0.875rem" }}>
                            Padrão
                          </Typography>
                        </>
                      )}
                    </Box>

                    {/* Container principal com borda azul/verde */}
                    <Box
                      sx={{
                        border: "2px solid #4db6ac",
                        borderRadius: 1,
                        p: 2,
                        pt: 6,
                        bgcolor: "white",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                    {/* Seção superior: DOIS containers lado a lado */}
                    <Box sx={{ display: "flex", gap: 2, minHeight: "120px", alignItems: "flex-start" }}>
                      {/* Container ESQUERDO: Informações do componente BASE */}
                      {/* TAREFA 11.7 (FASE 03): cartão interno em rosa-claro quando excluído. */}
                      <Box
                        sx={{
                          flex: 1,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          p: 1.5,
                          bgcolor: apontamento.excluida ? "#ffebee" : "#fafafa",
                          display: "flex",
                          flexDirection: "column",
                          alignSelf: "flex-start",
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
                          {/* Imagem do componente BASE */}
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "white",
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            {codigoBaseSelecionado.icone ? (
                              <img
                                src={buildImageSrc(codigoBaseSelecionado.icone)}
                                alt={codigoBaseSelecionado.codigoBase}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = "none";
                                }}
                              />
                            ) : (
                              <Box sx={{ width: "100%", height: "100%", bgcolor: "#f5f5f5" }} />
                            )}
                          </Box>

                          {/* Informações do componente BASE */}
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                                {codigoBaseSelecionado.codigoBase}
                              </Typography>
                              {codigoBaseSelecionado.fichaTecnica && (
                                <Tooltip title="Visualizar ficha técnica" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(codigoBaseSelecionado.fichaTecnica, "_blank", "noopener,noreferrer");
                                    }}
                                    sx={{ p: 0 }}
                                  >
                                    <DescriptionIcon fontSize="small" sx={{ color: "primary.main" }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ color: "primary.main", display: "block", mb: 0.3, fontSize: "0.75rem" }}>
                              {codigoBaseSelecionado.descricao}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "primary.main", display: "block", mb: 0.3 }}>
                              COR: {codigoBaseSelecionado.cor?.toUpperCase() || "N/A"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "primary.main", display: "block" }}>
                              REFERÊNCIA: {codigoBaseSelecionado.referencia || "MERCADO"}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Container para bases agrupadas e botão */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                          {/* Bases agrupadas */}
                          {apontamento.componentesBaseAgrupados && apontamento.componentesBaseAgrupados.length > 0 && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
                              {apontamento.componentesBaseAgrupados.map((baseAgrupada) => (
                                <Box
                                  key={baseAgrupada.idAplicabilidadeAgrupamentoBase}
                                  sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    p: 1.5,
                                    bgcolor: "#f9f9f9",
                                    position: "relative",
                                  }}
                                >
                                  {/* Imagem */}
                                  <Box
                                    sx={{
                                      width: 50,
                                      height: 50,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      bgcolor: "white",
                                      borderRadius: 1,
                                      border: "1px solid #e0e0e0",
                                      overflow: "hidden",
                                      flexShrink: 0,
                                    }}
                                  >
                                    {baseAgrupada.iconeBase ? (
                                      <img
                                        src={buildImageSrc(baseAgrupada.iconeBase)}
                                        alt={baseAgrupada.codigoBase}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "contain",
                                        }}
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).style.display = "none";
                                        }}
                                      />
                                    ) : (
                                      <Box sx={{ width: "100%", height: "100%", bgcolor: "#f5f5f5" }} />
                                    )}
                                  </Box>
                                  {/* Informações */}
                                  <Box sx={{ flex: 1 }}>
                                    {/* P-10 (Resposta PO 2026-05-12): ícone de ficha técnica
                                        ao lado do código, padrão idêntico aos cartões aplicáveis. */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                                        {baseAgrupada.codigoBase}
                                      </Typography>
                                      {baseAgrupada.fichaTecnicaBase && (
                                        <Tooltip title="Visualizar ficha técnica" arrow>
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              window.open(baseAgrupada.fichaTecnicaBase, "_blank", "noopener,noreferrer");
                                            }}
                                            sx={{ p: 0 }}
                                          >
                                            <DescriptionIcon fontSize="small" sx={{ color: "primary.main" }} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                    <Typography variant="caption" sx={{ color: "primary.main", display: "block", mb: 0.3, fontSize: "0.75rem" }}>
                                      {baseAgrupada.descricaoBase}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "primary.main", display: "block", mb: 0.3 }}>
                                      COR: {baseAgrupada.corBase?.toUpperCase() || "N/A"}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "primary.main", display: "block" }}>
                                      {baseAgrupada.referenciaBase || "MERCADO"}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {/* Botão no canto inferior esquerdo - Agrupa CÓDIGOS BASE (N Bases → 1 Aplicável) */}
                          <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", pr: 1, mt: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              disabled={apontamento.excluida || planilhaPendente}
                              onClick={() => handleAbrirModalAgrupamentoBases(apontamento.idAplicabilidade)}
                              sx={{
                                fontSize: "0.65rem",
                                py: 0.5,
                                px: 1,
                                borderColor: "#e0e0e0",
                                color: "#666",
                                textTransform: "none",
                              }}
                            >
                              + AGRUPAR BASES
                            </Button>
                          </Box>
                        </Box>
                      </Box>

                      {/* Container DIREITO: Informações do componente APLICÁVEL */}
                      {/* TAREFA 11.7 (FASE 03): cartão interno em rosa-claro quando excluído. */}
                      <Box
                        sx={{
                          flex: 1,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          p: 1.5,
                          bgcolor: apontamento.excluida ? "#ffebee" : "#fafafa",
                          display: "flex",
                          flexDirection: "column",
                          alignSelf: "flex-start",
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
                          {/* Imagem do componente APLICÁVEL */}
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "white",
                              borderRadius: 1,
                              border: "1px solid #e0e0e0",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            {apontamento.iconeAplicavel ? (
                              <img
                                src={buildImageSrc(apontamento.iconeAplicavel)}
                                alt={apontamento.codigoAplicavel}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = "none";
                                }}
                              />
                            ) : (
                              <Box sx={{ width: "100%", height: "100%", bgcolor: "#f5f5f5" }} />
                            )}
                          </Box>

                          {/* Informações do componente APLICÁVEL */}
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                                {apontamento.codigoAplicavel}
                              </Typography>
                              {apontamento.fichaTecnicaAplicavel && (
                                <Tooltip title="Visualizar ficha técnica" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(apontamento.fichaTecnicaAplicavel, "_blank", "noopener,noreferrer");
                                    }}
                                    sx={{ p: 0 }}
                                  >
                                    <DescriptionIcon fontSize="small" sx={{ color: "primary.main" }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ color: "primary.main", display: "block", mb: 0.3, fontSize: "0.75rem" }}>
                              {apontamento.descricaoAplicavel}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "primary.main", display: "block", mb: 0.3 }}>
                              COR: {apontamento.corAplicavel?.toUpperCase() || "N/A"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "primary.main", display: "block" }}>
                              {apontamento.referenciaAplicavel || "MERCADO"}
                            </Typography>
                          </Box>
                        </Box>

                              {/* Componentes agrupados */}
                              {(apontamento.componentesAgrupados && apontamento.componentesAgrupados.length > 0) && (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1, mb: 0 }}>
                                  {apontamento.componentesAgrupados.map((agrupado, idx) => (
                                    <Box
                                      key={agrupado.idAplicabilidadeAgrupamento}
                                      sx={{
                                        display: "flex",
                                        gap: 1.5,
                                        p: 1.5,
                                        bgcolor: "#f9f9f9",
                                        position: "relative",
                                      }}
                                    >
                                      {/* Imagem */}
                                      <Box
                                        sx={{
                                          width: 50,
                                          height: 50,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          bgcolor: "white",
                                          borderRadius: 1,
                                          border: "1px solid #e0e0e0",
                                          overflow: "hidden",
                                          flexShrink: 0,
                                        }}
                                      >
                                        {agrupado.iconeAplicavel ? (
                                          <img
                                            src={buildImageSrc(agrupado.iconeAplicavel)}
                                            alt={agrupado.codigoAplicavel}
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "contain",
                                            }}
                                            onError={(e) => {
                                              (e.target as HTMLImageElement).style.display = "none";
                                            }}
                                          />
                                        ) : (
                                          <Box sx={{ width: "100%", height: "100%", bgcolor: "#f5f5f5" }} />
                                        )}
                                      </Box>
                                      {/* Informações */}
                                      <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                                            {agrupado.codigoAplicavel}
                                          </Typography>
                                          {agrupado.fichaTecnicaAplicavel && (
                                            <Tooltip title="Visualizar ficha técnica" arrow>
                                              <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  window.open(agrupado.fichaTecnicaAplicavel, "_blank", "noopener,noreferrer");
                                                }}
                                                sx={{ p: 0 }}
                                              >
                                                <DescriptionIcon fontSize="small" sx={{ color: "primary.main" }} />
                                              </IconButton>
                                            </Tooltip>
                                          )}
                                        </Box>
                                        <Typography variant="caption" sx={{ color: "primary.main", display: "block", mb: 0.3, fontSize: "0.75rem" }}>
                                          {agrupado.descricaoAplicavel}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "primary.main", display: "block", mb: 0.3 }}>
                                          COR: {agrupado.corAplicavel?.toUpperCase() || "N/A"}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "primary.main", display: "block" }}>
                                          {agrupado.referenciaAplicavel || "MERCADO"}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              )}

                              {/* Botão no canto inferior direito - Agrupa CÓDIGOS APLICÁVEIS (1 Base → N Aplicáveis) */}
                              <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", pr: 1, mt: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  disabled={apontamento.excluida || planilhaPendente}
                                  onClick={() => handleAbrirModalAgrupamento(apontamento.idAplicabilidade, false)}
                                  sx={{
                                    fontSize: "0.65rem",
                                    py: 0.5,
                                    px: 1,
                                    borderColor: "#e0e0e0",
                                    color: "#666",
                                    textTransform: "none",
                                  }}
                                >
                                  + AGRUPAR COMPONENTES
                                </Button>
                              </Box>
                      </Box>
                    </Box>

                    {/* Seção inferior: Informações do apontamento */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1, color: "primary.main", fontSize: "0.875rem" }}>
                          Defina as linha(s)/sistema(s) para essa aplicabilidade
                        </Typography>
                        <FormControl fullWidth size="small">
                          <ReactSelect
                            isMulti
                            isClearable={false}
                            isDisabled={apontamento.excluida || planilhaPendente}
                            maxMenuHeight={290}
                            menuPosition="fixed"
                            options={linhasSistemaOptions}
                            value={(apontamento.linhasSistema || [])
                              .map((l) => linhasSistemaOptions.find((o) => o.value === l.idLinhaSistema))
                              .filter((o): o is { value: number; label: string } => Boolean(o))}
                            onChange={(selected) => {
                              const ids = ((selected as Array<{ value: number; label: string }>) || []).map((s) => s.value);
                              // FASE 03 (item 5): toda linha selecionada já é ativa/padrão.
                              const novasLinhas: ILinhaSistemaPadrao[] = ids.map((id) => ({ idLinhaSistema: id, isPadrao: true }));
                              // FASE 03 (item 4 — 22-06): NÃO grava na hora; marca pendência e habilita o SALVAR.
                              handleAlterarLinhasSistema(apontamento, novasLinhas);
                            }}
                            placeholder="Selecione linha(s)/sistema(s)"
                            noOptionsMessage={() => "Sem opções"}
                            styles={linhasSistemaSelectStyles}
                          />
                        </FormControl>
                        {/* FASE 03 (item 5): seção "Marque a linha/sistema padrão" removida —
                            a seleção da linha já a torna ativa para a aplicabilidade. */}
                      </Box>

                    </Box>
                  </Box>
                  </Box>

                  {/* Botão de remover - fora do box */}
                  {/* TAREFA 11.7 (FASE 03): tombstone (já excluído) não exibe ação de excluir. */}
                  {/* FASE 03 (item 4 / Pergunta 2 do PO — 22-06): marca/desmarca a remoção como pendente
                      (efetivada no SALVAR). Quando pendente, o botão fica preenchido (clique = desfazer). */}
                  {!apontamento.excluida && (
                    <Tooltip
                      title={remocoesPendentes.has(apontamento.idAplicabilidade)
                        ? "Remoção pendente — clique para desfazer (efetiva no SALVAR)"
                        : "Marcar para remoção (efetiva no SALVAR)"}
                      arrow
                    >
                      <span style={{ display: "inline-flex", flexShrink: 0 }}>
                        <IconButton
                          disabled={planilhaPendente}
                          onClick={() => handleToggleRemocaoPendente(apontamento.idAplicabilidade)}
                          sx={{
                            bgcolor: remocoesPendentes.has(apontamento.idAplicabilidade) ? "#d32f2f" : "#ffebee",
                            color: remocoesPendentes.has(apontamento.idAplicabilidade) ? "#fff" : "#d32f2f",
                            borderRadius: 1,
                            width: 36,
                            height: 36,
                            flexShrink: 0,
                            "&:hover": {
                              bgcolor: remocoesPendentes.has(apontamento.idAplicabilidade) ? "#b71c1c" : "#ffcdd2",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: "center" }}>
              Nenhum apontamento de aplicabilidade encontrado.
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Modal Adicionar/Editar Apontamento */}
      {empresaId && codigoBaseSelecionado && (
        <ModalApontamento
          open={modalApontamento.isOpen}
          onClose={() => setModalApontamento({ isOpen: false })}
          apontamento={modalApontamento.apontamento}
          idEmpresa={parseInt(empresaId)}
          idComponenteBase={codigoBaseSelecionado.idComponenteBase}
          codigoBase={codigoBaseSelecionado.codigoBase}
          isAgrupamento={modalApontamento.isAgrupamento}
          isAgrupamentoBases={modalApontamento.isAgrupamentoBases}
          idsJaAgrupados={
            modalApontamento.isAgrupamento && !modalApontamento.isAgrupamentoBases
              ? (apontamentos.find((a) => a.idAplicabilidade === modalApontamento.idAplicabilidadeParaAgrupar)
                  ?.componentesAgrupados || []).map((c) => c.idComponenteAplicavel)
              : undefined
          }
          onSave={async (selecao?: IComponenteAplicavel | IComponenteAplicavel[]) => {
            // Frente 10 (02/10/2026): agrupamento de aplicáveis (1 Base → N Aplicáveis) recebe um
            // ARRAY do modal (multi-seleção) e enfileira uma operação por item.
            if (modalApontamento.isAgrupamento && !modalApontamento.isAgrupamentoBases) {
              const lista = Array.isArray(selecao) ? selecao : selecao ? [selecao] : [];
              if (lista.length > 0) {
                handleAdicionarComponenteAgrupado(lista);
              }
              return;
            }

            // Demais usos: seleção única.
            const componenteAplicavel = Array.isArray(selecao) ? selecao[0] : selecao;

            // Agrupamento de bases é tratado pelo ModalAgrupamentoBases separadamente

            // TAREFA 8.3 (FASE 03): staging — preenche o cartão do item em edição sem persistir.
            // A gravação ocorre apenas no botão SALVAR (handleSalvarApontamentos).
            if (modalApontamento.itemEmEdicaoId && componenteAplicavel) {
              const idItem = modalApontamento.itemEmEdicaoId;
              setItensEmEdicao((prev) =>
                prev.map((i) =>
                  i.id === idItem
                    ? { ...i, componenteAplicavel, isDuplicado: false }
                    : i
                )
              );
              setModalApontamento({ isOpen: false });
            } else {
              // Se não há item em edição, é um apontamento existente sendo editado
              if (codigoBaseSelecionado) {
                await carregarApontamentos(codigoBaseSelecionado);
              }
            }
          }}
        />
      )}

      {/* Modal Agrupamento de Bases (N Bases → 1 Aplicável) */}
      {empresaId && codigoBaseSelecionado && (
        <ModalAgrupamentoBases
          open={modalAgrupamentoBases.isOpen}
          onClose={() => setModalAgrupamentoBases({ isOpen: false })}
          idEmpresa={parseInt(empresaId)}
          idComponenteBaseAtual={codigoBaseSelecionado.idComponenteBase}
          onSave={handleAdicionarBasesAgrupadas}
        />
      )}

      {/* Frente 9 (02/10/2026, decisão P2): aviso de aplicabilidade sem apontamento padrão.
          CANCELAR volta para a edição sem salvar; CONTINUAR segue para a confirmação normal. */}
      <Dialog open={confirmarSemPadraoOpen} onClose={() => setConfirmarSemPadraoOpen(false)}>
        <DialogTitle sx={{ bgcolor: "#3C90AD", color: "#fff", fontWeight: 600 }}>
          Aplicabilidade sem padrão
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mt: 2 }}>
            A aplicabilidade em edição não possui um apontamento como padrão, deseja continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmarSemPadraoOpen(false)} variant="outlined">
            CANCELAR
          </Button>
          <Button
            onClick={handleContinuarSemPadrao}
            variant="contained"
            sx={{ bgcolor: "#3C90AD", "&:hover": { bgcolor: "#2d7a94" } }}
          >
            CONTINUAR
          </Button>
        </DialogActions>
      </Dialog>

      {/* TAREFA 8.7 (FASE 03) + Plano 05/10/2026 (Frente 7): confirmação exibida SÓ quando há apontamento
          idêntico a um já existente; SALVAR grava os demais e mantém os duplicados em vermelho. */}
      <Dialog open={confirmarSalvarOpen} onClose={() => setConfirmarSalvarOpen(false)}>
        <DialogTitle>Salvar aplicabilidades</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Há apontamentos idênticos a um já existente: eles estão destacados em vermelho e não serão
            gravados. Deseja gravar os demais?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarSalvarOpen(false)} variant="outlined">
            CANCELAR
          </Button>
          <Button onClick={handleConfirmarSalvar} variant="contained" color="success">
            SALVAR
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardStackCustom>
  );
};

